/**
 * FieldTooltip — [T72P2-a]
 *
 * A small help-circle icon that shows contextual tooltip text when hovered.
 * Designed to sit inline with FormLabel text to provide field-level guidance.
 */

import { useId, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

interface FieldTooltipProps {
  /** Help text shown in the tooltip. */
  text: string;
  /** Preferred tooltip placement. */
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export function FieldTooltip({ text, side = 'top' }: FieldTooltipProps) {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger
          asChild
          onClick={(event) => {
            // Mobile / tap toggle. Prevent default so the underlying form-control
            // focus does not steal the tooltip before it has a chance to open.
            event.preventDefault();
            setOpen((prev) => !prev);
          }}
          onPointerEnter={() => setOpen(true)}
          onPointerLeave={() => setOpen(false)}
        >
          <HelpCircle
            className="inline h-3.5 w-3.5 ml-1 text-muted-foreground cursor-help align-middle"
            role="img"
            aria-label="Help"
            aria-describedby={tooltipId}
          />
        </TooltipTrigger>
        <TooltipContent
          id={tooltipId}
          side={side}
          className="max-w-[240px] text-xs leading-relaxed"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
